import { z } from 'zod';
import { inquiryAddressSchema } from '../inquiry/address.js';
import { reportSummarySchema } from '../inspection/report.js';
import { inspectionResultSchema } from '../inspection/result.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * L'**export client** — la dernière étape du cycle de vie métier, « validé → exporté client » :
 * le fichier que GHP transmet à Nantes Métropole ou aux Mauges.
 *
 * ⚠️ **Ce qui s'exporte est une demande `validated`, pas une `Inspection`.** La table
 * `Inspection` (M16 → M20) n'existe pas, et `Inspection.exported_at` avec elle : la trace de
 * livraison vit donc sur sa propre table, `ClientExport`, dont
 * [M26](../../../../docs/MODELE-DONNEES.md#m26) porte le motif et le devenir. Le contenu du
 * contrôle vient, comme partout ailleurs au bureau, de la **proposition** déposée par le terrain
 * (D4).
 */
/**
 * Le filtre de la file d'export : ce qui reste à livrer, ce qui est parti, ou les deux.
 *
 * ⚠️ `'all'` est une **valeur du filtre**, pas une absence — même règle que les filtres du
 * tableau des demandes : « pas de filtre » s'écrit dans l'URL et se lit dans un journal.
 */
export const CLIENT_EXPORT_DELIVERIES = ['all', 'pending', 'delivered'];
export const clientExportDeliverySchema = z.enum(CLIENT_EXPORT_DELIVERIES);
/**
 * Le **regroupement** de la file — le « Trier » du prototype validé (`exportClients`,
 * `mocks/ghp-bo/screens.js`) : par client et lot, par client, par commune, ou à plat par période.
 *
 * ⚠️ **C'est un tri serveur, pas une mise en forme locale**, comme le regroupement par équipe de
 * la file « À valider » : seul l'ordre de la requête rend un groupe contigu, **y compris d'une
 * page à l'autre**. Regrouper la page reçue laisserait le même lot éclaté sur trois pages.
 *
 * `lot` d'abord, parce que c'est le défaut du prototype : une livraison se prépare lot par lot.
 */
/**
 * L'état de **saisie** d'un contrôle validé — l'onglet « Saisie » du prototype : les données du
 * rapport reprises et vérifiées au bureau avant l'export définitif. `pending` : pas encore faite ;
 * `done` : terminée (« Saisie terminée »). Une validation **nouvelle** ouvre une saisie ; les
 * contrôles validés avant son introduction sont repris comme faits (migration `0039`).
 */
export const CLIENT_EXPORT_ENTRIES = ['all', 'pending', 'done'];
export const clientExportEntrySchema = z.enum(CLIENT_EXPORT_ENTRIES);
export const CLIENT_EXPORT_GROUPINGS = ['lot', 'client', 'city', 'period'];
export const clientExportGroupingSchema = z.enum(CLIENT_EXPORT_GROUPINGS);
/**
 * Une ligne de la file d'export : un contrôle validé, ce qu'on sait de son relevé, et sa
 * dernière livraison.
 *
 * L'adresse est aplatie et nullable pour la même raison qu'ailleurs — voir
 * `inquiryListRowSchema` : c'est la même chaîne de jointure, et les colonnes de l'existant sont
 * trouées (M21).
 */
export const clientExportRowSchema = z.object({
    inquiryId: uuidV7Schema,
    /** La case « Urgent » du bureau (Sentry BACK-OFFICE-X), dite en rouge sur la ligne. */
    urgent: z.boolean().default(false),
    /** Le rang de contre-visite — 1, 2… —, `null` pour un premier contrôle (#321, Q5). */
    followUpRank: z.number().int().positive().nullable().default(null),
    reference: z.string().nullable(),
    caseReference: z.string().nullable(),
    /** Le lien vers le dossier client (#410) — affiché sur la ligne, jamais mis dans le fichier. */
    caseUrl: z.string().nullable(),
    /**
     * Le **donneur d'ordre** — la première colonne du prototype sur cet écran, celle par laquelle
     * il range sa file.
     *
     * ⚠️ **Mêmes deux chemins que la liste des demandes, même arbitrage** : `Inquiry.client_id`
     * (M32) l'emporte sur `Site.lot_id → Lot.client_id` (M14). Une livraison se prépare client par
     * client, et se tromper de client est la seule erreur que cet écran ne rattrape pas — le
     * fichier est parti.
     */
    client: z
        .object({
        id: uuidV7Schema,
        name: z.string(),
    })
        .nullable(),
    address: inquiryAddressSchema,
    /**
     * Le **lot** du lieu contrôlé (`Site.lot_id`, M14) — ce par quoi la file se regroupe par défaut,
     * et ce que l'en-tête de groupe du prototype nomme (« Nantes Métropole · Lot 2025-04 »).
     *
     * `null` pour un lieu hors campagne : une vente entre particuliers n'appartient à aucun lot, et
     * elle s'exporte quand même.
     */
    lot: z
        .object({
        code: z.string(),
        name: z.string(),
    })
        .nullable(),
    /**
     * Le **type de contrôle**, colonne « Type » du prototype sur cet écran.
     *
     * Le **code**, pas le libellé : la colonne est du texte libre (M2, M10), et `inquiryPurposeLabel`
     * porte le repli sur la valeur brute qu'un type ajouté par GHP demande. Stocker le libellé
     * figerait la valeur le jour où « Vente » est renommée.
     *
     * `nullable` parce que la colonne l'est : l'existant a des demandes sans type, et les écarter
     * de la file ne les livrerait jamais (M21).
     */
    purpose: z.string().nullable(),
    team: z
        .object({
        id: uuidV7Schema,
        name: z.string(),
    })
        .nullable(),
    /**
     * Le résumé du **dernier relevé transmis**, ou `null` quand la demande n'en porte aucun de
     * lisible.
     *
     * ⚠️ Un objet nullable, et non trois champs nullables côte à côte : les trois valeurs sont
     * absentes **ensemble**, pour une seule raison, et trois `null` indépendants laisseraient
     * croire à trois manques distincts. Le cas existe — une demande passée `validated` sans
     * qu'aucune tablette ait déposé, ou dont la charge utile ne se relit plus (M21) : la ligne
     * reste exportable, et le fichier dira le trou plutôt que de le taire.
     *
     * ⚠️ C'est une **proposition** (D4), pas une `Inspection` : le bureau valide, mais rien
     * n'écrit encore de contrôle en base.
     */
    proposal: z
        .object({
        submittedAt: z.iso.datetime({ offset: true }),
        /**
         * La conclusion du contrôle : celle que l'agent a **posée à la main**, ou à défaut celle
         * que les anomalies du relevé déduisent (`resolveInspectionResult`, M19).
         *
         * ⚠️ Aucune des deux n'est stockée sous cette forme — la surcharge voyage dans la
         * proposition et la dérivation se recalcule. La colonne qui les rangerait est sur
         * `Inspection`, table qui n'existe pas encore (#235) : ce fichier est donc **recomposé** à
         * chaque export, et il suit une règle de M19 corrigée entre-temps.
         */
        result: inspectionResultSchema,
        anomalyCount: z.number().int().min(0),
    })
        .nullable(),
    /**
     * La **dernière** livraison au client, ou `null` si le contrôle n'est jamais parti. C'est ce
     * qui empêche de livrer deux fois sans le savoir — et c'est bien la dernière, pas la première :
     * une relivraison est un fait normal (le client a perdu le fichier), et c'est sa date qui
     * répond à « quand l'a-t-il reçu ».
     */
    exportedAt: z.iso.datetime({ offset: true }).nullable(),
    /**
     * Le rapport PDF du dernier contrôle, s'il est **généré** — `null` tant que la file de jobs ne
     * l'a pas produit. C'est ce qui permet à la ligne de désarmer son icône « Fiche PDF » au lieu de
     * mener à un `404`.
     */
    report: reportSummarySchema.nullable(),
    /** Quand la saisie a été terminée — `null` : en attente, la ligne entre dans l'onglet « Saisie ». */
    entryDoneAt: z.iso.datetime({ offset: true }).nullable(),
});
/**
 * Ce que le serveur accepte pour construire une page de la file — la **seule** définition dont
 * dérivent l'URL de l'écran, le `WHERE` du dépôt et l'appel du client typé (D24).
 *
 * ⚠️ Schéma **strict** : la tolérance M21 vit à l'entrée HTTP
 * (`apps/api/src/exports/client-export-query.ts`), pas dans le modèle — même découpage que le
 * tableau des demandes.
 */
export const clientExportFilterSchema = z.object({
    delivery: clientExportDeliverySchema,
    /**
     * Le filtre « Client » de la file : on livre un client à la fois (#661, qui remplace le filtre
     * « Marché »). Par identifiant, par les deux chemins du donneur d'ordre, comme le tableau des
     * demandes (`inquiryListFilterSchema`).
     */
    clientId: z.union([z.literal('all'), uuidV7Schema]),
    /**
     * La recherche libre de la file — référence GHP, référence du dossier, rue, commune, donneur
     * d'ordre : ce que la ligne affiche, comme celle du tableau des demandes. Vide = aucune.
     */
    search: z.string(),
    /**
     * Le **type de contrôle** — l'onglet « Ventes » du prototype est `sale`. Champ libre au modèle
     * (M2, M13), donc une chaîne et non une énumération ; `all` = aucun filtre.
     */
    purpose: z.string(),
    /** L'état de saisie (`clientExportEntrySchema`) — l'onglet « Saisie » demande `pending`. */
    entry: clientExportEntrySchema,
    grouping: clientExportGroupingSchema,
    limit: z.number().int().positive(),
    offset: z.number().int().min(0),
});
/**
 * Ce que rend « Tout exporter » : les identifiants de **tout** l'ensemble filtré, dans l'ordre de la
 * file, au plus `MAX_INQUIRIES_PER_EXPORT` — le plafond d'un export.
 *
 * ⚠️ **`truncated` dit que la liste est coupée**, et l'écran doit le montrer : exporter les 500
 * premiers d'un ensemble qui en compte 612 sans le dire serait livrer un fichier incomplet.
 */
export const clientExportSelectionSchema = z.object({
    inquiryIds: z.array(uuidV7Schema),
    total: z.number().int().min(0),
    truncated: z.boolean(),
});
/**
 * Les effectifs des deux onglets, comptés sur **l'ensemble** des contrôles validés — jamais sur
 * la page affichée (D24). `all` porte la file entière, `pending` ce qui n'est pas encore parti :
 * « déjà livrés » se déduit des deux, et une troisième requête compterait un nombre déjà connu.
 */
export const clientExportCountsSchema = z.object({
    all: z.number().int().min(0),
    pending: z.number().int().min(0),
    /**
     * Les **ventes** de l'ensemble — le compteur de l'onglet « Ventes » (`purpose = 'sale'`). Compté
     * comme `all`, sans le filtre de livraison ni celui du type : c'est ce que l'onglet montrerait.
     */
    sale: z.number().int().min(0),
    /**
     * La pastille de l'onglet « Saisie » : les contrôles dont la saisie attend, **livrés ou non** —
     * `saisie && !sent` au prototype, où `sent` ne se pose qu'à l'archivage : un export ne termine
     * aucune saisie.
     */
    entryPending: z.number().int().min(0),
});
/** L'enveloppe paginée de la file d'export (D24) — même forme que celle du tableau des demandes. */
export const clientExportPageSchema = z.object({
    items: z.array(clientExportRowSchema),
    /** L'effectif de l'onglet **actif**, sur l'ensemble filtré. Jamais `items.length`. */
    total: z.number().int().min(0),
    counts: clientExportCountsSchema,
});
/**
 * Ce qu'un fichier porte au grand maximum.
 *
 * ⚠️ Il borne l'**amplification**, pas le métier : un export lit une ligne de demande et une
 * proposition par contrôle demandé, et rend un fichier que le serveur compose en mémoire. Cinq
 * cents contrôles font déjà un lot mensuel très large ; sans plafond, un seul appel demanderait
 * la table entière. Le refus tombe au schéma — donc dans un `400` qui nomme le champ (M21).
 */
export const MAX_INQUIRIES_PER_EXPORT = 500;
/**
 * Ce que le bureau demande à livrer : **une liste d'identifiants**, jamais un filtre à rejouer
 * côté serveur (M14 § 5). Ce que l'utilisateur a coché est ce sur quoi il agit — un filtre rejoué
 * livrerait les lignes arrivées entre l'affichage et le clic.
 */
export const clientExportRequestSchema = z.object({
    inquiryIds: z.array(uuidV7Schema).min(1).max(MAX_INQUIRIES_PER_EXPORT),
});
/**
 * Ce que le bureau demande à **archiver** : la même liste d'identifiants, et pour la même
 * raison — ce qu'on a coché est ce sur quoi on agit.
 *
 * ⚠️ **« Valider & archiver » est un geste à part de la livraison**, comme au prototype
 * (`data-exp-send`, `mocks/ghp-bo/screens.js:1169`, qui fait passer le contrôle dans les
 * Archives). Archiver automatiquement à l'export retirerait au gestionnaire la décision de
 * clore : on livre souvent avant d'avoir la confirmation du client.
 *
 * ⚠️ C'est **la dernière marche du cycle de vie** (M3, `validated → archived`), et rien dans le
 * dépôt ne la franchissait : l'écran « Archives » lisait un statut que personne n'écrivait.
 */
export const archiveInquiriesSchema = z.object({
    inquiryIds: z.array(uuidV7Schema).min(1).max(MAX_INQUIRIES_PER_EXPORT),
});
/**
 * Ce que l'archivage rend : **combien de contrôles sont passés aux archives**.
 *
 * ⚠️ Un nombre inférieur à la demande n'est pas une erreur silencieuse — le service refuse
 * d'abord ce qui n'est plus archivable. C'est le rejeu qui rend moins : deux onglets ouverts, et
 * le second trouve les lignes déjà archivées. Zéro se dit (M21).
 */
export const archivedInquiriesSchema = z.object({
    archived: z.number().int().min(0),
});
//# sourceMappingURL=client-export.js.map